/**
 * One driver, day by day.
 *
 * The screen the office opens with a driver standing in front of it. The four tiles across
 * the top are what the conversation starts from; the rows underneath are what settles it,
 * and behind each row are the sign-on and sign-off times that produced it.
 *
 * Three things on this screen exist purely to answer a question a driver will ask:
 * a day below the minimum is shown, marked, and charged nothing; a session the system or
 * the office ended says so; and a week whose cap bit shows the uncapped figure beside it.
 *
 * The payments list is not fetched with the report. It is one more request for a screen
 * most people open to read the days rather than the receipts, and it is behind a button for
 * that reason alone.
 */

import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { describe } from '../settings/errors';
import { useTenantFormats } from '../shell/formats';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  StatTile,
  Sub,
  Table,
  Td,
  Th,
  Tr,
} from '../ui';
import { reportsApi, type DriverPayment, type DriverShiftBook } from './api';

export function ShiftBookDriver({
  report,
  openDay,
  onBack,
  onSettings,
}: {
  report: DriverShiftBook;
  /** The day to start expanded, when this page was reached by clicking a grid cell. */
  openDay: string | null;
  onBack: () => void;
  onSettings: () => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const [open, setOpen] = useState<string | null>(openDay);

  const money = report.currency;
  const inMoney = (amount: number) => (money === null ? '—' : formats.money(amount));

  return (
    <>
      <div className="flex items-center gap-3">
        <Button variant="quiet" onClick={onBack}>
          <Icon name="back" small />
          {t('reports.shiftBook.backToFleet')}
        </Button>

        <h2 className="text-section font-semibold m-0">
          {report.displayName}
          {report.callSign !== null && <Sub> · {report.callSign}</Sub>}
          {!report.isActive && <Sub> · {t('reports.shiftBook.left')}</Sub>}
        </h2>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <Card>
          <StatTile
            label={t('reports.shiftBook.daysWorked')}
            value={formats.count(report.daysWorked)}
            // `count` picks the plural; `shown` is what is printed. i18next interpolates
            // with `String()`, which would bypass the tenant's own grouping.
            sub={t('reports.shiftBook.daysChargedSub', {
              count: report.daysCharged,
              shown: formats.count(report.daysCharged),
            })}
          />
        </Card>

        <Card>
          <StatTile
            label={t('reports.shiftBook.hoursOnDuty')}
            value={formats.hours(report.minutes)}
            sub={t('reports.shiftBook.breakSub', { hours: formats.hours(report.breakMinutes) })}
          />
        </Card>

        <Card>
          <StatTile
            label={t('reports.shiftBook.amountOwed')}
            value={inMoney(report.charged)}
            sub={
              money === null
                ? undefined
                : t('reports.shiftBook.paidSub', { amount: formats.money(report.paidInPeriod) })
            }
          />
        </Card>

        <Card>
          <StatTile
            label={t('reports.shiftBook.outstandingAllTime')}
            value={inMoney(report.outstanding)}
            tone={report.outstanding > 0 ? 'warn' : 'neutral'}
          />
        </Card>
      </div>

      {money === null && (
        <Banner
          tone="warn"
          actions={
            <Button size="sm" onClick={onSettings}>
              {t('reports.shiftBook.openSettings')}
            </Button>
          }
        >
          {t('reports.shiftBook.noRate')}
        </Banner>
      )}

      {report.currenciesDiffer && (
        <Banner tone="warn">{t('reports.shiftBook.mixedCurrency')}</Banner>
      )}

      <Card>
        <CardHeader title={t('reports.shiftBook.byDay')} />

        {report.days.length === 0 ? (
          // This page is headed with one man's name, and is usually open with him standing
          // at the desk. The fleet-wide "nobody was on duty" answers a question about the
          // firm, which is not the question that was asked.
          <EmptyState>{t('reports.shiftBook.notOnDuty')}</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <Table tight>
              <thead>
                <tr>
                  <Th scope="col">{t('reports.shiftBook.day')}</Th>
                  <Th scope="col" numeric>
                    {t('reports.shiftBook.hours')}
                  </Th>
                  <Th scope="col" numeric>
                    {t('reports.shiftBook.break')}
                  </Th>
                  <Th scope="col" numeric>
                    {t('reports.shiftBook.sessions')}
                  </Th>
                  <Th scope="col">{t('reports.shiftBook.chargeable')}</Th>
                  <Th scope="col" numeric>
                    {t('reports.shiftBook.cost', { currency: money ?? formats.currency })}
                  </Th>
                  <Th scope="col" numeric>
                    {t('reports.shiftBook.rides')}
                  </Th>
                  <Th scope="col" numeric>
                    {t('reports.shiftBook.takings', { currency: money ?? formats.currency })}
                  </Th>
                </tr>
              </thead>

              <tbody>
                {report.days.map((day) => (
                  <Fragment key={day.date}>
                    {/* A day below the minimum reads differently from one not worked at all
                        — it is the first thing a driver asks about, and "it does not say
                        anything" is not an answer. */}
                    <Tr className={day.chargeable ? undefined : 'text-ink-3 italic'}>
                      <Td header>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 cursor-pointer hover:underline"
                          aria-expanded={open === day.date}
                          onClick={() => setOpen(open === day.date ? null : day.date)}
                        >
                          <Icon name={open === day.date ? 'chevron' : 'chevron-right'} small />
                          {formats.dayLong(day.date)}
                        </button>
                      </Td>
                      <Td numeric>{formats.hours(day.minutes)}</Td>
                      <Td numeric>{formats.hours(day.breakMinutes)}</Td>
                      <Td numeric>{formats.count(day.sessions.length)}</Td>
                      <Td>
                        {day.chargeable
                          ? t('reports.shiftBook.yes')
                          : t('reports.shiftBook.belowMinimum')}
                      </Td>
                      <Td numeric>{day.charge === null ? '—' : formats.amount(day.charge)}</Td>
                      <Td numeric>{formats.count(day.takings.rides)}</Td>
                      <Td numeric>
                        {formats.amount(
                          day.takings.cash +
                            day.takings.card +
                            day.takings.account +
                            day.takings.unrecorded,
                        )}
                      </Td>
                    </Tr>

                    {open === day.date && (
                      <Tr>
                        <Td colSpan={8} className="bg-surface-2" wrap>
                          <ul className="flex flex-col gap-1 list-none m-0 p-0">
                            {day.sessions.map((session) => (
                              <li key={session.startedAt}>
                                {formats.time(session.startedAt)} —{' '}
                                {formats.time(session.endedAt)}
                                <Sub> · {formats.hours(session.minutes)}</Sub>
                                {session.ending !== 'SignedOff' && (
                                  // Exactly the day somebody asks about, so the screen says
                                  // which of the four it was rather than looking like an
                                  // ordinary sign-off.
                                  <strong className="font-semibold">
                                    {' '}
                                    · {t(`reports.shiftBook.ending.${session.ending}`)}
                                  </strong>
                                )}
                              </li>
                            ))}
                          </ul>
                        </Td>
                      </Tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader title={t('reports.shiftBook.weeks')} />

        <div className="overflow-x-auto">
          <Table>
            <thead>
              <tr>
                <Th scope="col">{t('reports.shiftBook.weekStarting')}</Th>
                <Th scope="col" numeric>
                  {t('reports.shiftBook.daysCharged')}
                </Th>
                <Th scope="col" numeric>
                  {t('reports.shiftBook.uncapped', { currency: money ?? formats.currency })}
                </Th>
                <Th scope="col" numeric>
                  {t('reports.shiftBook.cap')}
                </Th>
                <Th scope="col" numeric>
                  {t('reports.shiftBook.charged', { currency: money ?? formats.currency })}
                </Th>
              </tr>
            </thead>

            <tbody>
              {report.weeks.map((week) => (
                <Tr key={week.weekStart}>
                  <Td header>
                    {formats.day(week.weekStart)}
                    {week.partial && (
                      // A firm reading a capped partial week as a discount they did not give
                      // is the misunderstanding this exists to prevent.
                      <Sub> · {t('reports.shiftBook.partialWeek')}</Sub>
                    )}
                  </Td>
                  <Td numeric>{formats.count(week.chargeableDays)}</Td>
                  <Td numeric>{formats.amount(week.uncapped)}</Td>
                  <Td numeric>{week.cap === null ? '—' : formats.amount(week.cap)}</Td>
                  <Td numeric>
                    {formats.amount(week.charged)}
                    {week.capped && <Sub> · {t('reports.shiftBook.capApplied')}</Sub>}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card>

      <Payments driverId={report.driverId} />
    </>
  );
}

/**
 * What has been handed over, and what was put right.
 *
 * A payment is never edited and never deleted. A wrong one is answered by a row saying so,
 * carrying a reason, which is why a reversal appears in this list rather than removing the
 * line above it — and why the reason is asked for in a dialog that will not let the button
 * be pressed without one.
 */
function Payments({ driverId }: { driverId: string }) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const [payments, setPayments] = useState<DriverPayment[] | null>(null);
  const [reversing, setReversing] = useState<DriverPayment | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const reversed = new Set(
    (payments ?? []).map((payment) => payment.reversesPaymentId).filter((id) => id !== null),
  );

  const load = () => {
    setBusy(true);
    setProblem(null);

    void reportsApi
      .payments(driverId)
      .then(setPayments)
      .catch((error: unknown) => setProblem(describe(error, t, 'settings.couldNotLoad')))
      .finally(() => setBusy(false));
  };

  const reverse = () => {
    if (reversing === null || reason.trim().length === 0) return;

    setBusy(true);
    setProblem(null);

    void reportsApi
      .reversePayment(reversing.id, reason.trim())
      .then(() => {
        setReversing(null);
        setReason('');
        load();
      })
      .catch((error: unknown) => {
        setProblem(describe(error, t, 'settings.couldNotSave'));
        setBusy(false);
      });
  };

  return (
    <Card>
      <CardHeader
        title={t('reports.shiftBook.payments')}
        actions={
          payments === null ? (
            <Button size="sm" disabled={busy} onClick={load}>
              {t('reports.shiftBook.showPayments')}
            </Button>
          ) : null
        }
      />

      {payments === null ? (
        <EmptyState>{t('reports.shiftBook.paymentsHidden')}</EmptyState>
      ) : payments.length === 0 ? (
        <EmptyState>{t('reports.shiftBook.noPayments')}</EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <thead>
              <tr>
                <Th scope="col">{t('reports.shiftBook.paidAt')}</Th>
                <Th scope="col" numeric>
                  {t('reports.shiftBook.amount')}
                </Th>
                <Th scope="col">{t('reports.shiftBook.note')}</Th>
                <Th scope="col" />
              </tr>
            </thead>

            <tbody>
              {payments.map((payment) => (
                <Tr
                  key={payment.id}
                  className={payment.amount < 0 ? 'text-danger' : undefined}
                >
                  <Td header>{formats.moment(payment.paidAt)}</Td>
                  <Td numeric>{formats.amount(payment.amount)}</Td>
                  <Td wrap>
                    {payment.reversalReason ?? payment.note ?? ''}
                    {payment.periodFrom !== null && payment.periodTo !== null && (
                      <Sub>
                        {' '}
                        {formats.day(payment.periodFrom)} — {formats.day(payment.periodTo)}
                      </Sub>
                    )}
                  </Td>
                  <Td>
                    {payment.amount > 0 && !reversed.has(payment.id) && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          setProblem(null);
                          setReason('');
                          setReversing(payment);
                        }}
                      >
                        {t('reports.shiftBook.reverse')}
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {problem !== null && reversing === null && (
        <Banner tone="danger" footer>
          {problem}
        </Banner>
      )}

      <Dialog
        open={reversing !== null}
        title={t('reports.shiftBook.reverseTitle')}
        onClose={() => setReversing(null)}
        actions={
          <>
            <Button variant="quiet" onClick={() => setReversing(null)}>
              {t('reports.shiftBook.cancel')}
            </Button>
            <Button
              variant="danger"
              // The reason is what the row will carry for the rest of the firm's life, so
              // an empty one is refused here rather than by the server.
              disabled={busy || reason.trim().length === 0}
              onClick={reverse}
            >
              {t('reports.shiftBook.reverseConfirm')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {reversing !== null && (
            <p className="text-secondary text-ink-3 m-0">
              {formats.moment(reversing.paidAt)} · {formats.amount(reversing.amount)}
            </p>
          )}

          <Field label={t('reports.shiftBook.reverseReason')}>
            <Input value={reason} onChange={(event) => setReason(event.target.value)} />
          </Field>

          {problem !== null && <Banner tone="danger">{problem}</Banner>}
        </div>
      </Dialog>
    </Card>
  );
}
